import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckSquare, Square, ChevronRight, RotateCcw } from "lucide-react";

const TABS = [
  { parentType: "lesson_name", childType: "topic",          parentLabel: "Ders",     childLabel: "Konu" },
  { parentType: "topic",       childType: "subtopic",        parentLabel: "Konu",     childLabel: "Alt Konu" },
  { parentType: "subtopic",    childType: "unit_name",       parentLabel: "Alt Konu", childLabel: "Ünite" },
  { parentType: "unit_name",   childType: "taxonomy_level_1",parentLabel: "Ünite",    childLabel: "Taksonomi 1" },
];

function SectionTitle({ children }) {
  return (
    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
      {children}
    </p>
  );
}

export default function TaxonomyManager() {
  const [activeTab, setActiveTab] = useState(0);
  const [allValues, setAllValues] = useState({});
  const [pairs, setPairs] = useState({});        // { parentValue: [childValues] }
  const [selectedParent, setSelectedParent] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(new Set()); // child values being toggled

  const tab = TABS[activeTab];

  // Load all distinct values (for parent + child lists)
  useEffect(() => {
    api.get("/taxonomy/all-values")
      .then((r) => setAllValues(r.data))
      .catch(() => toast.error("Değerler yüklenemedi"));

    api.get("/taxonomy/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  // Load pairs when tab changes
  const loadPairs = useCallback(async () => {
    setLoading(true);
    setSelectedParent("");
    try {
      const res = await api.get("/taxonomy/pairs", {
        params: { parent_type: tab.parentType, child_type: tab.childType },
      });
      setPairs(res.data);
    } catch {
      toast.error("İlişkiler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [tab.parentType, tab.childType]);

  useEffect(() => { loadPairs(); }, [loadPairs]);

  const parentOptions = allValues[tab.parentType] || [];
  const allChildOptions = allValues[tab.childType] || [];
  const selectedChildren = new Set(pairs[selectedParent] || []);

  const handleToggle = async (childValue) => {
    if (!selectedParent) return;
    const isChecked = selectedChildren.has(childValue);
    const key = childValue;
    setToggling((prev) => new Set(prev).add(key));

    const body = {
      parent_type: tab.parentType,
      parent_value: selectedParent,
      child_type: tab.childType,
      child_value: childValue,
    };

    try {
      if (isChecked) {
        await api.delete("/taxonomy/relationship", { data: body });
        setPairs((prev) => {
          const next = { ...prev };
          next[selectedParent] = (next[selectedParent] || []).filter((v) => v !== childValue);
          return next;
        });
      } else {
        await api.post("/taxonomy/relationship", body);
        setPairs((prev) => {
          const next = { ...prev };
          next[selectedParent] = [...(next[selectedParent] || []), childValue].sort();
          return next;
        });
      }
    } catch {
      toast.error("Değişiklik kaydedilemedi");
    } finally {
      setToggling((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const handleSync = async () => {
    if (!window.confirm("Mevcut sorulardan tüm hiyerarşi ilişkileri otomatik doldurulsun mu? Mevcut manuel düzenlemeler korunur, sadece eksik ilişkiler eklenir.")) return;
    setSyncing(true);
    try {
      const res = await api.post("/taxonomy/sync");
      toast.success(`Senkronizasyon tamamlandı: ${res.data.new_relationships_added} yeni ilişki eklendi (${res.data.total_pairs_processed} çift işlendi)`);
      await loadPairs();
      const statsRes = await api.get("/taxonomy/stats");
      setStats(statsRes.data);
    } catch {
      toast.error("Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Taksonomi Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Ders → Konu → Alt Konu → Ünite → Taksonomi hiyerarşisini tanımlayın
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          data-testid="sync-taxonomy-btn"
          variant="outline"
          className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Senkronize ediliyor..." : "Sorulardan Otomatik Doldur"}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(stats.by_level).map(([key, count]) => (
            <div key={key} className="bg-white border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 font-medium">{key.replace("_name", "").replace("_", " → ").replace("lesson", "Ders").replace("topic→subtopic", "Konu → Alt Konu").replace("subtopic→unit_name", "Alt Konu → Ünite").replace("unit_name→taxonomy", "Ünite → Taksonomi")}</p>
              <p className="text-xl font-bold text-slate-800 mt-1">{count.toLocaleString()}</p>
              <p className="text-[10px] text-slate-400">ilişki</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          {TABS.map((t, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              data-testid={`taxonomy-tab-${i}`}
              className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === i
                  ? "border-blue-500 text-blue-700 bg-blue-50/50"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="text-xs text-slate-400 hidden sm:inline">{t.parentLabel}</span>
              <ChevronRight size={11} className="text-slate-300 hidden sm:inline" />
              <span>{t.childLabel}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-5 min-h-[480px]">
          {/* Left: Parent list */}
          <div className="col-span-2 border-r border-slate-100 overflow-y-auto">
            <div className="sticky top-0 bg-slate-50/80 backdrop-blur px-4 py-2.5 border-b border-slate-100">
              <SectionTitle>{tab.parentLabel} Seç</SectionTitle>
              {selectedParent && (
                <button
                  onClick={() => setSelectedParent("")}
                  className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1"
                >
                  <RotateCcw size={9} /> Seçimi temizle
                </button>
              )}
            </div>
            {loading ? (
              <div className="p-4 space-y-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            ) : parentOptions.length === 0 ? (
              <p className="p-4 text-xs text-slate-400">Henüz {tab.parentLabel} bulunamadı</p>
            ) : (
              <ul className="py-1">
                {parentOptions.map((opt) => {
                  const childCount = (pairs[opt] || []).length;
                  return (
                    <li key={opt}>
                      <button
                        onClick={() => setSelectedParent(opt)}
                        data-testid={`parent-${opt}`}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between group ${
                          selectedParent === opt
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span className="truncate pr-2">{opt}</span>
                        {childCount > 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${
                            selectedParent === opt ? "bg-blue-200 text-blue-700" : "bg-slate-100 text-slate-500"
                          }`}>
                            {childCount}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Right: Children with checkboxes */}
          <div className="col-span-3 overflow-y-auto">
            <div className="sticky top-0 bg-slate-50/80 backdrop-blur px-4 py-2.5 border-b border-slate-100">
              {selectedParent ? (
                <div className="flex items-center justify-between">
                  <SectionTitle>
                    <span className="normal-case text-slate-600 font-semibold text-xs">{selectedParent}</span>
                    <span className="text-slate-400 ml-1">→ {tab.childLabel}</span>
                  </SectionTitle>
                  <span className="text-[10px] text-slate-400">{selectedChildren.size} seçili / {allChildOptions.length} toplam</span>
                </div>
              ) : (
                <SectionTitle>← Soldaki listeden {tab.parentLabel} seçin</SectionTitle>
              )}
            </div>

            {!selectedParent ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-300">
                <ChevronRight size={32} />
                <p className="text-sm mt-2">Bir {tab.parentLabel} seçin</p>
              </div>
            ) : allChildOptions.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-slate-400">
                  Sistemde henüz {tab.childLabel} kaydı bulunamadı.
                </p>
                <p className="text-xs text-slate-300 mt-1">
                  Sorulardan otomatik doldur butonunu kullanın.
                </p>
              </div>
            ) : (
              <ul className="py-1 divide-y divide-slate-50">
                {allChildOptions.map((child) => {
                  const checked = selectedChildren.has(child);
                  const busy = toggling.has(child);
                  return (
                    <li key={child}>
                      <label
                        data-testid={`child-${child}`}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors select-none ${
                          checked ? "bg-teal-50/60" : "hover:bg-slate-50"
                        } ${busy ? "opacity-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => handleToggle(child)}
                          className="sr-only"
                        />
                        <span className={`flex-shrink-0 w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                          checked ? "bg-teal-500 border-teal-500" : "border-slate-300 bg-white"
                        }`}>
                          {checked && (
                            <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 fill-white">
                              <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                            </svg>
                          )}
                          {busy && <div className="w-2.5 h-2.5 border border-slate-300 border-t-transparent rounded-full animate-spin" />}
                        </span>
                        <span className={`text-sm ${checked ? "text-teal-700 font-medium" : "text-slate-600"}`}>
                          {child}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-400 text-center">
        Checkbox'a tıklayınca değişiklik anında kaydedilir. "Sorulardan Otomatik Doldur" butonu sadece eksik ilişkileri ekler, mevcut düzenlemeleri silmez.
      </p>
    </div>
  );
}
